import { supabase } from '../supabase';

/**
 * Authenticate user with wallet address
 * @param {string} walletAddress - The user's wallet address
 * @returns {Promise<Object>} User data or error
 */
export async function authenticateWithWallet(walletAddress) {
  if (!walletAddress) {
    return { error: 'No wallet address provided' };
  }
  
  try {
    // Check if user exists
    const { data: existingUser, error: fetchError } = await supabase
      .from('users')
      .select('id, wallet_address, created_at, last_login')
      .eq('wallet_address', walletAddress)
      .single();
      
    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error checking user:', fetchError);
      return { error: fetchError };
    }
    
    if (existingUser) {
      // User exists, update last login time
      const { error: updateError } = await supabase
        .from('users')
        .update({ last_login: new Date() })
        .eq('wallet_address', walletAddress);
        
      if (updateError) {
        console.error('Error updating last login:', updateError);
      }
      
      return { user: existingUser, isNewUser: false };
    } else {
      // Create new user
      const { data: newUser, error: insertError } = await supabase
        .from('users')
        .insert([{ wallet_address: walletAddress }])
        .select()
        .single();
        
      if (insertError) {
        console.error('Error creating user:', insertError);
        return { error: insertError };
      }
      
      return { user: newUser, isNewUser: true };
    }
  } catch (error) {
    console.error('Unexpected error during authentication:', error);
    return { error };
  }
}

/**
 * Check if a user is authenticated
 * @param {string} userId - User ID to check
 * @returns {Promise<Object>} User data or error
 */
export async function checkAuthentication(userId) {
  if (!userId) {
    return { authenticated: false, error: 'No user ID provided' };
  }
  
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, wallet_address, created_at, last_login')
      .eq('id', userId)
      .single();
      
    if (error) {
      console.error('Error checking authentication:', error);
      return { authenticated: false, error };
    }
    
    return { authenticated: true, user: data };
  } catch (error) {
    console.error('Error checking authentication:', error);
    return { authenticated: false, error };
  }
} 